// Typed fetch wrappers for the Vibe Worker at navi-vibe.com.
// PWA is hosted on phone.navi-vibe.com → same apex → cookies cross-domain
// over `credentials: 'include'`.

import type { MusicGenre, MusicMood } from '../music/types';

const BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://navi-vibe.com';

/* ───── Existing Mochi-simulator types (kept for the legacy PalTab) ───── */

export interface DeviceRow {
    id: number;
    mac_suffix: string;
    variant: string;
    pal_name: string | null;
    last_seen_at: number | null;
    created_at: number;
}

export interface RegisterArgs {
    mac_suffix: string;
    variant: string;
    pal_name?: string;
}

/* ───── Vibe types (mirror webapp/src/types.ts; slim subset) ───── */

export type ToneVariant =
    | 'minimal' | 'friendly' | 'hype' | 'premium'
    | 'funny' | 'analytical' | 'gentle';

export type VeilMode = 'full' | 'soft' | 'circle' | 'open';
export type MusicVibeSharing = 'off' | 'places_only' | 'places_and_routes';

export interface SignalToggles {
    experience_sharing: boolean;
    music_vibe_sharing: MusicVibeSharing;
    presence_sharing: boolean;
    alert_participation: boolean;
    trend_signals: boolean;
    query_participation: boolean;
}

export interface VibeClassScore {
    name: string;
    score: number;
    since: string;
}

export interface ActiveQuestProgress {
    id: string;
    progress: number;
    of: number;
}

export interface VibeProfile {
    schema_version: 2;
    pal_id: string;
    user_id_hash: string;
    pal_name: string;
    pal_age_days: number;
    vibe_classes: VibeClassScore[];
    active_form: string;
    active_accessories: string[];
    current_goals: string[];
    avoidances: string[];
    tone_preference: ToneVariant;
    language_mix: string[];
    cheekiness_dial: number;
    privacy_mode: VeilMode;
    signal_toggles: SignalToggles;
    circles_joined: string[];
    glow: number;
    marks_count: number;
    active_quests: ActiveQuestProgress[];
    created_at: string;
    last_updated: string;
    source: string;
}

export interface PalProfileResponse {
    pal_id: string;
    pal_name: string;
    pal_age_days: number;
    glow: number;
    marks_count: number;
    profile: VibeProfile;
    created_at: number;
}

export interface OnboardingRequest {
    pal_name: string;
    vibe_classes: string[];
    preferences: { crowds: number; novelty: number; pace: number };
    tone_preference: ToneVariant;
    privacy_mode: VeilMode;
    signal_toggles: SignalToggles;
}

export type RenderTrigger = 'anticipation' | 'reflection' | 'direct_query';
export type RenderIntent =
    | 'lifestyle' | 'general' | 'code' | 'learning' | 'writing' | 'chat';

export interface RenderRequest {
    trigger: RenderTrigger;
    query?: string;
    thread_id?: string;
    voice?: boolean;
    context?: {
        location_cell?: string;
        time_of_day?: 'morning' | 'afternoon' | 'evening' | 'night';
        domain?: string;
        intent_hint?: RenderIntent;
    };
}

export interface RenderResponse {
    render_id: string;
    thread_id: string;
    text: string;
    intent: RenderIntent;
    evidence_count: number;
    resonance: {
        score: number;
        breakdown: Record<string, number>;
    } | null;
    usage: {
        input_tokens: number;
        output_tokens: number;
        cache_read_input_tokens: number;
        cache_creation_input_tokens: number;
    };
}

export interface DailyBrief {
    date: string;
    ready: boolean;
    reason?: 'no_echo_yet';
    headline: string;
    body: string;
    memory: {
        status: 'preview' | 'private' | 'none';
        text: string | null;
        path: string | null;
    };
    quest: {
        id: string;
        title: string;
        progress: number;
        of: number;
    } | null;
    last_echo: {
        trace_id: string;
        node_id: string;
        node_name: string;
        domain: string;
        created_at: number;
        qualitative: string[];
    } | null;
    activity_card: {
        id: string;
        vertical: string;
        memory_type: string;
        path: string;
        title: string;
        body: string;
        updated_at: number;
    } | null;
    collection_card: {
        id: string;
        title: string;
        progress: number;
        target: number;
        completed: boolean;
        next_action: string;
    } | null;
}

export type DailyAction =
    | { kind: 'drive'; label: string }
    | { kind: 'map'; label: string; node_id?: string }
    | { kind: 'ask_moji'; label: string; prompt?: string }
    | { kind: 'settings'; label: string; target: 'voice' | 'music_vibe' | 'privacy' };

export interface DailyReturnReactionPart {
    kind: ReactionKind;
    count: number;
}

export type DailyReturnCard =
    | { kind: 'empty_state'; id: string; title: string; body: string; action: DailyAction }
    | {
        kind: 'activity_memory';
        id: string;
        vertical: string;
        memory_type: string;
        path: string;
        title: string;
        body: string;
        updated_at: number;
    }
    | {
        kind: 'collection_progress';
        id: string;
        title: string;
        progress: number;
        target: number;
        completed: boolean;
        next_action: string;
    }
    | { kind: 'quest_progress'; id: string; title: string; progress: number; of: number }
    | {
        kind: 'reaction_summary';
        id: string;
        total: number;
        parts: DailyReturnReactionPart[];
        latest_at: number | null;
        target_echo: string | null;
    }
    | {
        kind: 'last_echo';
        id: string;
        trace_id: string;
        node_id: string;
        node_name: string;
        domain: string;
        created_at: number;
        qualitative: string[];
    }
    | { kind: 'map_suggestion'; id: string; node_id: string; title: string; body: string }
    | { kind: 'privacy_nudge'; id: string; title: string; body: string; action: DailyAction };

export interface DailyReturnV2 {
    date: string;
    headline: string;
    body: string;
    primary_action: DailyAction | null;
    cards: DailyReturnCard[];
}

export interface GrowthMemoryCard {
    id: string;
    path: string;
    summary: string;
    memory_type: string;
    updated_at: number;
}

export type GrowthRing =
    | {
        kind: 'driving';
        level: number;
        label: string;
        stats: {
            saved_drives: number;
            clean_drive_streak: number;
            latest_smoothness_pct?: number;
        };
    }
    | {
        kind: 'places';
        level: number;
        label: string;
        stats: {
            save_count: number;
            visit_count: number;
            drive_count: number;
            echo_count: number;
        };
    }
    | {
        kind: 'social';
        level: number;
        label: string;
        stats: {
            echo_count: number;
            reaction_count: number;
        };
    }
    | {
        kind: 'music';
        level: number;
        label: string;
        stats: {
            music_vibe_mode: MusicVibeSharing;
            music_tagged_echo_count: number;
        };
    };

export interface ActivityGrowthSummary {
    generated_at: number;
    rings: GrowthRing[];
    memories: GrowthMemoryCard[];
    collections: StampCollectionProgress[];
    privacy: {
        experience_sharing: boolean;
        music_vibe_sharing: MusicVibeSharing;
        voice_opt_in: 'local_only';
    };
}

export type FeedbackAction =
    | 'more_like' | 'less_like' | 'forget' | 'remember'
    | 'wrong' | 'tone_quieter' | 'tone_louder';

export interface QuestSummary {
    id: string;
    title: string;
    description: string;
    category: 'personal' | 'discovery' | 'circle' | 'partner' | 'seasonal';
    domain: string | null;
    goal: { target_visits: number } | null;
    reward: { marks?: string[]; glow?: number; accessory_unlock?: string };
    progress?: { visits: number; contributing_node_ids: string[] };
}

export interface QuestsListResponse {
    active: QuestSummary[];
    completed: Array<QuestSummary & { completed_at: number }>;
    available: QuestSummary[];
    glow: number;
    marks_count: number;
}

export interface PalProfilePatch {
    pal_name?: string;
    tone_preference?: ToneVariant;
    privacy_mode?: VeilMode;
    signal_toggles?: Partial<SignalToggles>;
    current_goals_append?: string[];
    avoidances_append?: string[];
}

export type DriverSpeedBucket = 'unknown' | 'under_60' | '60_90' | '90_120' | '120_plus';
export type DistanceBucket = 'under_5km' | '5_15km' | '15_30km' | '30km_plus';
export type EngineRpmBucket = 'unknown' | 'low' | 'medium' | 'high';
export type ThrottleBucket = 'unknown' | 'gentle' | 'moderate' | 'heavy';
export type NodeStampType = 'save' | 'visit' | 'drive' | 'echo';
export type MapBucket = '24h' | '7d' | '30d';

export interface StampCollectionProgress {
    id: string;
    title: string;
    description: string;
    progress: number;
    target: number;
    completed: boolean;
    goal: { target_distinct_nodes: number; domains?: string[]; tags_any?: string[] };
    reward: { marks?: string[]; glow?: number; accessory_unlock?: string };
}

export interface ActivityDriverTripRequest {
    node_id?: string;
    occurred_at?: number;
    smoothness: number;
    hard_brakes: number;
    max_speed_bucket: DriverSpeedBucket;
    distance_bucket?: DistanceBucket;
    duration_bucket?: string;
    time_bucket?: string;
    avg_rpm_bucket?: EngineRpmBucket;
    throttle_bucket?: ThrottleBucket;
}

export interface ActivityDriverTripResponse {
    event: { id: string; occurred_at: number; expires_at: number };
    memories: Array<{
        id: string;
        path: string;
        summary: string;
        memory_type: string;
        updated_at: number;
    }>;
    stamp: {
        id: string;
        node_id: string;
        stamp_type: NodeStampType;
        stamp_count: number;
    } | null;
    collections: StampCollectionProgress[];
}

export interface EchoShareBody {
    node_id: string;
    activity_event_id?: string;
    qualitative: string[];
    discomfort?: string[];
    duration_bucket?: string;
    time_bucket?: string;
    day_bucket?: string;
    weather_bucket?: string;
    season?: string;
    interpretation?: string;
    music_genre?: MusicGenre;
    music_mood?: MusicMood;
}

export type ReactionKind = 'heart' | 'sparkle' | 'note';

export interface ReactionCounts {
    heart: number;
    sparkle: number;
    note: number;
}

export interface NodeRecentEcho {
    trace_id: string;
    pal_form: string;
    qualitative_tags: string[];
    music_genre: MusicGenre | null;
    music_mood: MusicMood | null;
    created_at: number;
    is_mine: boolean;
    reactions: ReactionCounts;
}

export interface ReactionDeltaItem {
    target_echo: string;
    kind: ReactionKind;
    new_count: number;
    latest_at: number;
}

export interface MapCell {
    h3: string;
    centroid: { lat: number; lon: number };
    boundary: Array<{ lat: number; lng: number }>;
    echo_count_bin: number;
    node_count: number | null;
    dominant_form: string | null;
    dominant_qualitative: string[];
}

export interface MapCellsResponse {
    bucket: MapBucket;
    snapshot_run_at: number | null;
    snapshot_next_at: number | null;
    res: number;
    min_k_cell: number;
    min_k_detail: number;
    cells: MapCell[];
}

export interface MapCellDetail {
    h3: string;
    bucket: MapBucket;
    snapshot_run_at: number;
    echo_count_bin: number;
    boundary: Array<{ lat: number; lng: number }>;
    qualitative_top5: Array<[string, number]>;
    form_breakdown: Array<[string, number]>;
    time_buckets: Record<string, number | null>;
    nodes_in_cell: Array<{ id: string; name: string; domain: string }>;
}

export interface MapNode {
    id: string;
    domain: string;
    name: string;
    locality: string | null;
    tags: string[];
    lat: number;
    lng: number;
    guide_count: number;
    stamps: Array<{ stamp_type: NodeStampType; stamp_count: number; last_stamped_at: number }>;
    source: 'owned' | 'public';
}

export interface NodeInsightResponse {
    node: { id: string; domain: string; name: string; locality: string | null; tags: string[] };
    guide_summary: {
        entry_count: number;
        items: Array<{
            id: string;
            guide_source: string;
            qualitative: string[];
            discomfort: string[];
            interpretation: string | null;
            confidence: number;
            signal_provenance: 'guide';
        }>;
        top_qualitative: Array<{ tag: string; count: number; signal_provenance: 'guide' }>;
    };
    network_summary: {
        available: boolean;
        contributor_count: number;
        k_anonymity_threshold: number;
        top_qualitative: Array<{ tag: string; count: number; signal_provenance: 'network' }>;
        top_discomfort: Array<{ tag: string; count: number; signal_provenance: 'network' }>;
    };
    my_history: {
        echo_count: number;
        last_echo_at: number | null;
        top_qualitative: Array<{ tag: string; count: number; signal_provenance: 'personal' }>;
    };
    music_vibe: {
        k: number;
        dominant_genre: MusicGenre | 'mixed';
        dominant_mood: MusicMood | null;
        dominant_share: number;
    } | null;
    signal_strength: 'none' | 'guide_only' | 'below_k' | 'network';
    privacy_note: string;
}

/* ───── Internal helper ───── */

async function jsonOrThrow<T>(r: Response): Promise<T> {
    if (r.ok) return (await r.json()) as T;
    let err = `http_${r.status}`;
    try {
        const body = await r.json() as { err?: string };
        if (body.err) err = body.err;
    } catch { /* non-JSON body */ }
    const e = new Error(err) as Error & { status?: number };
    e.status = r.status;
    throw e;
}

export class ApiClient {
    constructor(private readonly base: string = BASE) {}

    /* Auth */

    async authRequest(email: string): Promise<void> {
        const returnTo = typeof window !== 'undefined' ? window.location.origin : '';
        const r = await fetch(`${this.base}/api/auth/request`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, return_to: returnTo || undefined }),
        });
        await jsonOrThrow<{ ok: true }>(r);
    }

    async signOut(): Promise<void> {
        const r = await fetch(`${this.base}/api/auth/signout`, {
            method: 'POST',
            credentials: 'include',
        });
        await jsonOrThrow<{ ok: true }>(r);
    }

    /* Devices (legacy Mochi simulator path; kept for compatibility) */

    async devicesList(): Promise<DeviceRow[]> {
        const r = await fetch(`${this.base}/api/devices`, { credentials: 'include' });
        const body = await jsonOrThrow<{ items: DeviceRow[] }>(r);
        return body.items;
    }

    async deviceRegister(args: RegisterArgs): Promise<DeviceRow> {
        const r = await fetch(`${this.base}/api/devices/register`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(args),
        });
        const body = await jsonOrThrow<{ device: DeviceRow }>(r);
        return body.device;
    }

    /* Vibe Pal — identity */

    /** Returns null when the user hasn't onboarded yet (404). */
    async palMe(): Promise<PalProfileResponse | null> {
        const r = await fetch(`${this.base}/api/pals/me`, { credentials: 'include' });
        if (r.status === 404) return null;
        return jsonOrThrow<PalProfileResponse>(r);
    }

    async palOnboard(req: OnboardingRequest): Promise<PalProfileResponse> {
        const r = await fetch(`${this.base}/api/pals/onboard`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(req),
        });
        return jsonOrThrow<PalProfileResponse>(r);
    }

    async palPatch(patch: PalProfilePatch): Promise<PalProfileResponse> {
        const r = await fetch(`${this.base}/api/pals/me`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(patch),
        });
        return jsonOrThrow<PalProfileResponse>(r);
    }

    async palReset(): Promise<void> {
        const r = await fetch(`${this.base}/api/pals/me/reset`, {
            method: 'POST',
            credentials: 'include',
        });
        await jsonOrThrow<{ ok: true }>(r);
    }

    /* Render */

    async render(req: RenderRequest): Promise<RenderResponse> {
        const r = await fetch(`${this.base}/api/render`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(req),
        });
        return jsonOrThrow<RenderResponse>(r);
    }

    async dailyBrief(): Promise<DailyBrief> {
        const r = await fetch(`${this.base}/api/daily/brief`, { credentials: 'include' });
        return jsonOrThrow<DailyBrief>(r);
    }

    async dailyReturn(): Promise<DailyReturnV2> {
        const r = await fetch(`${this.base}/api/daily/return`, { credentials: 'include' });
        return jsonOrThrow<DailyReturnV2>(r);
    }

    /* Feedback */

    async feedback(args: { render_id: string; action: FeedbackAction; reason?: string }): Promise<{ ok: true; effects: string[] }> {
        const r = await fetch(`${this.base}/api/feedback`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(args),
        });
        return jsonOrThrow<{ ok: true; effects: string[] }>(r);
    }

    /* Quests */

    async questsList(): Promise<QuestsListResponse> {
        const r = await fetch(`${this.base}/api/quests`, { credentials: 'include' });
        return jsonOrThrow<QuestsListResponse>(r);
    }

    async questStart(id: string): Promise<{ ok: true }> {
        const r = await fetch(`${this.base}/api/quests/${encodeURIComponent(id)}/start`, {
            method: 'POST',
            credentials: 'include',
        });
        return jsonOrThrow<{ ok: true }>(r);
    }

    /* Nodes + Echoes (Chunk 7 driving vertical uses these) */

    async nodeSearch(args: { domain?: string; q?: string; locality?: string; limit?: number }): Promise<{ items: Array<{ id: string; name: string; domain: string; locality: string | null }> }> {
        const params = new URLSearchParams();
        if (args.domain) params.set('domain', args.domain);
        if (args.q) params.set('q', args.q);
        if (args.locality) params.set('locality', args.locality);
        if (args.limit) params.set('limit', String(args.limit));
        const r = await fetch(`${this.base}/api/nodes/search?${params}`, { credentials: 'include' });
        return jsonOrThrow<{ items: Array<{ id: string; name: string; domain: string; locality: string | null }> }>(r);
    }

    async nodeUpsert(args: {
        lat: number;
        lng: number;
        name: string;
        locality?: string;
    }): Promise<{ node_id: string; created: boolean }> {
        const r = await fetch(`${this.base}/api/nodes/upsert`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(args),
        });
        return jsonOrThrow<{ node_id: string; created: boolean }>(r);
    }

    async echoShare(args: EchoShareBody): Promise<{ trace_id: string; expires_at: number; quest_deltas: Array<{ questId: string; title: string; progress: { visits: number; contributing_node_ids: string[] }; target: number; completedNow: boolean; rewards?: { glow?: number; marks?: string[] } }> }> {
        const r = await fetch(`${this.base}/api/echoes/share`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(args),
        });
        return jsonOrThrow(r);
    }

    async activityDriverTrip(args: ActivityDriverTripRequest): Promise<ActivityDriverTripResponse> {
        const r = await fetch(`${this.base}/api/activity/driver/trips`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(args),
        });
        return jsonOrThrow<ActivityDriverTripResponse>(r);
    }

    async activitySummary(): Promise<{ memories: ActivityDriverTripResponse['memories']; collections: StampCollectionProgress[] }> {
        const r = await fetch(`${this.base}/api/activity/summary`, { credentials: 'include' });
        return jsonOrThrow<{ memories: ActivityDriverTripResponse['memories']; collections: StampCollectionProgress[] }>(r);
    }

    async activityGrowth(): Promise<ActivityGrowthSummary> {
        const r = await fetch(`${this.base}/api/activity/growth`, { credentials: 'include' });
        return jsonOrThrow<ActivityGrowthSummary>(r);
    }

    async activityMemoryDelete(id: string): Promise<{ ok: true; mirror_path: string | null }> {
        const r = await fetch(`${this.base}/api/activity/memories/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            credentials: 'include',
        });
        return jsonOrThrow<{ ok: true; mirror_path: string | null }>(r);
    }

    async nodeStamp(nodeId: string, stampType: Extract<NodeStampType, 'save' | 'visit'>): Promise<{
        stamp: { id: string; node_id: string; stamp_type: NodeStampType; stamp_count: number };
        collections: StampCollectionProgress[];
    }> {
        const r = await fetch(`${this.base}/api/nodes/${encodeURIComponent(nodeId)}/stamp`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ stamp_type: stampType }),
        });
        return jsonOrThrow(r);
    }

    async mapCells(args: {
        west: number;
        south: number;
        east: number;
        north: number;
        bucket?: MapBucket;
        res?: number;
    }): Promise<MapCellsResponse> {
        const params = new URLSearchParams();
        params.set('west', String(args.west));
        params.set('south', String(args.south));
        params.set('east', String(args.east));
        params.set('north', String(args.north));
        if (args.bucket) params.set('bucket', args.bucket);
        if (args.res) params.set('res', String(args.res));
        const r = await fetch(`${this.base}/api/map/cells?${params}`, { credentials: 'include' });
        return jsonOrThrow<MapCellsResponse>(r);
    }

    async mapCell(h3: string, bucket: MapBucket): Promise<MapCellDetail> {
        const params = new URLSearchParams({ bucket });
        const r = await fetch(`${this.base}/api/map/cell/${encodeURIComponent(h3)}?${params}`, {
            credentials: 'include',
        });
        return jsonOrThrow<MapCellDetail>(r);
    }

    async nodeMap(args: {
        west?: number;
        south?: number;
        east?: number;
        north?: number;
        domain?: string;
        mine?: boolean;
        limit?: number;
    } = {}): Promise<{ items: MapNode[] }> {
        const params = new URLSearchParams();
        if (args.west !== undefined) params.set('west', String(args.west));
        if (args.south !== undefined) params.set('south', String(args.south));
        if (args.east !== undefined) params.set('east', String(args.east));
        if (args.north !== undefined) params.set('north', String(args.north));
        if (args.domain) params.set('domain', args.domain);
        if (args.mine) params.set('mine', '1');
        if (args.limit) params.set('limit', String(args.limit));
        const suffix = params.toString();
        const r = await fetch(`${this.base}/api/nodes/map${suffix ? `?${suffix}` : ''}`, {
            credentials: 'include',
        });
        return jsonOrThrow<{ items: MapNode[] }>(r);
    }

    async nodeInsight(nodeId: string): Promise<NodeInsightResponse> {
        const r = await fetch(`${this.base}/api/nodes/${encodeURIComponent(nodeId)}/insight`, {
            credentials: 'include',
        });
        return jsonOrThrow<NodeInsightResponse>(r);
    }

    async nodeRecentEchoes(nodeId: string): Promise<{ items: NodeRecentEcho[] }> {
        const r = await fetch(`${this.base}/api/nodes/${encodeURIComponent(nodeId)}/recent-echoes`, {
            credentials: 'include',
        });
        return jsonOrThrow<{ items: NodeRecentEcho[] }>(r);
    }

    async echoReact(traceId: string, kind: ReactionKind): Promise<{ added: boolean }> {
        const r = await fetch(`${this.base}/api/echoes/${encodeURIComponent(traceId)}/react`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kind }),
        });
        return jsonOrThrow<{ added: boolean }>(r);
    }

    async reactionsDelta(args: { since?: number; cursor?: string } = {}): Promise<{
        items: ReactionDeltaItem[];
        next_cursor: string | null;
    }> {
        const params = new URLSearchParams();
        if (args.since !== undefined) params.set('since', String(args.since));
        if (args.cursor) params.set('cursor', args.cursor);
        const suffix = params.toString();
        const r = await fetch(`${this.base}/api/echoes/mine/reactions-delta${suffix ? `?${suffix}` : ''}`, {
            credentials: 'include',
        });
        return jsonOrThrow<{ items: ReactionDeltaItem[]; next_cursor: string | null }>(r);
    }

    async stampCollections(): Promise<{ items: StampCollectionProgress[] }> {
        const r = await fetch(`${this.base}/api/stamps/collections`, { credentials: 'include' });
        return jsonOrThrow<{ items: StampCollectionProgress[] }>(r);
    }
}

export const api = new ApiClient();
